import { useInfiniteQuery, useQuery } from '@tanstack/react-query';
import { Button, Card, Notice } from '@ailab/ui';
import { balanceSchema, cursorPageSchema, movementSchema, type Movement } from '@ailab/contracts';
import { apiRequest, errorMessage } from '../../lib/http';
import styles from './account.module.css';

// Saldo y movimientos (RF-02.01, RF-02.02). Los importes vienen del ledger del servidor.

const movementsPage = cursorPageSchema(movementSchema);
const credits = new Intl.NumberFormat('es-ES');
const when = new Intl.DateTimeFormat('es-ES', { dateStyle: 'medium', timeStyle: 'short' });

export const MOVEMENT_LABELS: Record<Movement['type'], string> = {
  purchase: 'Compra',
  promotion: 'Promoción',
  adjustment: 'Ajuste',
  reservation: 'Reserva',
  settlement: 'Consumo',
  release: 'Reserva liberada',
  freeze: 'Saldo bloqueado',
  unfreeze: 'Saldo desbloqueado',
  legacy_import: 'Traslado desde la versión anterior',
  refund: 'Devolución',
};

function signed(value: number) {
  return `${value > 0 ? '+' : value < 0 ? '−' : ''}${credits.format(Math.abs(value))}`;
}

/** Lo que el movimiento cambió en el disponible y, si aplica, en lo retenido o bloqueado. */
export function describeMovement(movement: Movement): string {
  const parts = [`${signed(movement.available)} disponibles`];
  if (movement.held !== 0) parts.push(`${signed(movement.held)} retenidos`);
  if (movement.frozen !== 0) parts.push(`${signed(movement.frozen)} bloqueados`);
  return parts.join(' · ');
}

export function BalanceSection() {
  const balance = useQuery({
    queryKey: ['me', 'balance'],
    queryFn: () => apiRequest('/me/balance', balanceSchema),
  });
  const movements = useInfiniteQuery({
    queryKey: ['me', 'movements'],
    queryFn: ({ pageParam }) =>
      apiRequest(`/me/movements?limit=20${pageParam ? `&cursor=${pageParam}` : ''}`, movementsPage),
    initialPageParam: '',
    getNextPageParam: (last) => last.nextCursor ?? undefined,
  });
  const items = movements.data?.pages.flatMap((page) => page.items) ?? [];

  return (
    <Card>
      <h2 className={styles.sectionTitle}>Saldo</h2>
      {balance.isPending ? <p role="status">Cargando el saldo…</p> : null}
      {balance.isError ? <Notice tone="error">{errorMessage(balance.error)}</Notice> : null}
      {balance.data ? (
        <dl className={styles.balances}>
          <div>
            <dt>Disponible</dt>
            <dd>{credits.format(balance.data.available)} créditos</dd>
          </div>
          <div>
            <dt>Retenido en operaciones en curso</dt>
            <dd>{credits.format(balance.data.held)} créditos</dd>
          </div>
          {balance.data.frozen > 0 ? (
            <div>
              <dt>Bloqueado por membresía</dt>
              <dd>{credits.format(balance.data.frozen)} créditos</dd>
            </div>
          ) : null}
        </dl>
      ) : null}

      <h3 className={styles.subTitle}>Movimientos</h3>
      {movements.isError ? <Notice tone="error">{errorMessage(movements.error)}</Notice> : null}
      {movements.isSuccess && items.length === 0 ? <p>Todavía no hay movimientos.</p> : null}
      <ul className={styles.sessionList}>
        {items.map((movement) => (
          <li key={movement.id} className={styles.session}>
            <div>
              <strong>{MOVEMENT_LABELS[movement.type]}</strong>
              <div className={styles.sessionMeta}>
                {when.format(new Date(movement.createdAt))}
                {movement.reason ? ` · ${movement.reason}` : ''}
              </div>
            </div>
            <span className={styles.amount}>{describeMovement(movement)}</span>
          </li>
        ))}
      </ul>
      {movements.hasNextPage ? (
        <Button type="button" variant="ghost" onClick={() => void movements.fetchNextPage()}>
          Ver más
        </Button>
      ) : null}
    </Card>
  );
}
